import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import type { IYoutube } from "../../../../pages/Party/YoutubeMatchingInfo";
import LineBtn from "../../../Button/LineBtn";
import { Input } from "../../../Input/Input";
import DefaultModalLayout from "../../../Modal/DefaultModalLayout";
import YoutubeLinkModal, { YoutubeLinkState } from "./YoutubeLinkModal";
import { generateYoutubeLink } from "./youtubeLinkApi";
import { saveYoutubeUrlAllocation } from "./youtubeExtrainfoApi";

export default function YoutubeRoundLinkModal({ round, closeEvent, onSaved }: {
  round: IYoutube;
  closeEvent: () => void;
  onSaved: () => void;
}) {
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [generation, setGeneration] = useState<YoutubeLinkState | null>(null);

  const generate = async () => {
    const email = round.yt_r_email?.trim();
    if (!email) {
      setError("새 링크를 생성하려면 회차계정을 먼저 입력해 주세요.");
      return;
    }
    setGeneration({ email, status: "loading" });
    try {
      const result = await generateYoutubeLink(email, round.provider);
      setGeneration({ email, status: "success", result });
    } catch (error) {
      setGeneration({ email, status: "error", message: error instanceof Error ? error.message : "링크 생성에 실패했습니다." });
    }
  };

  const save = async () => {
    if (savingRef.current) return;
    const nextLink = link.trim();
    try {
      const url = new URL(nextLink);
      if (!["http:", "https:"].includes(url.protocol)) throw new Error();
    } catch {
      setError("신규링크를 http:// 또는 https://로 시작하는 주소로 입력해 주세요.");
      return;
    }
    const roundNumber = Number(round.yt_round);
    const status = Number(round.yt_r_status);
    if (!round.partyid || !round.uid || !Number.isInteger(roundNumber) || roundNumber < 1 || roundNumber > 12 || ![1, 2, 3].includes(status)) {
      setError("현재 회차 정보를 확인할 수 없습니다. 목록을 새로고침해 주세요.");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const result = await saveYoutubeUrlAllocation({
        provider: round.provider ?? undefined,
        pid: String(round.partyid), uid: String(round.uid),
        email: round.yt_r_email ?? "", round: roundNumber,
        r_status: status, r_link: nextLink,
      });
      if (result.round !== roundNumber || result.r_status !== status) throw new Error();
    } catch {
      setError("회차링크 변경에 실패했습니다. 다시 시도해 주세요.");
      return;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    toast.success("회차링크가 변경되었습니다.");
    onSaved();
  };

  if (generation) {
    return <YoutubeLinkModal state={generation} closeEvent={() => {
      if (generation.status !== "loading") setGeneration(null);
    }} />;
  }

  return (
    <DefaultModalLayout
      modalTitle="회차링크 변경"
      isMediumModal
      cancelBtnText="취소"
      confirmBtnText={saving ? "저장 중…" : "확인"}
      confirmDisabled={saving}
      cancelBtnDisabled={saving}
      closeEvent={() => { if (!savingRef.current) closeEvent(); }}
      confirmEvent={() => void save()}
      leftBottomBtn={<></>}
      contentPadding="24px 32px"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", lineHeight: 1.5 }}>
        <div>
          <div>현재링크</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ overflowWrap: "anywhere", minWidth: 0 }}>{round.yt_r_link || "-"}</span>
            <LineBtn
              type="button" icon="content_copy" aria-label="현재링크 복사" title="현재링크 복사"
              disabled={!round.yt_r_link || saving}
              style={{ flexShrink: 0 }}
              btnClickEvent={async () => {
                try {
                  await navigator.clipboard.writeText(round.yt_r_link ?? "");
                  toast.success("회차링크가 복사되었습니다.");
                } catch {
                  toast.error("회차링크 복사에 실패했습니다.");
                }
              }}
            />
          </div>
        </div>
        <div>
          <label htmlFor="youtube-new-round-link">신규링크</label>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Input id="youtube-new-round-link" type="url" value={link} disabled={saving}
              placeholder="변경할 링크 입력" style={{ minWidth: 0 }}
              onChange={(event) => { setLink(event.target.value); setError(""); }} />
            <LineBtn type="button" btnText="새 링크 생성" disabled={saving}
              style={{ flexShrink: 0 }} btnClickEvent={() => void generate()} />
          </div>
        </div>
        {error && <p role="alert">{error}</p>}
      </div>
    </DefaultModalLayout>
  );
}
